import Modal from 'Common/Components/Modal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import React, { FC, useEffect, useRef, useState } from 'react';

const DEFAULT_SCANNER_ELEMENT_ID = 'barcode-scanner-root';

// Medicine barcode formats only (1D barcodes) — no QR code
export const MEDICINE_BARCODE_FORMATS = [
    Html5QrcodeSupportedFormats.EAN_13,
    Html5QrcodeSupportedFormats.EAN_8,
    Html5QrcodeSupportedFormats.CODE_128,
    Html5QrcodeSupportedFormats.CODE_39,
    Html5QrcodeSupportedFormats.CODE_93,
    Html5QrcodeSupportedFormats.UPC_A,
    Html5QrcodeSupportedFormats.UPC_E,
    Html5QrcodeSupportedFormats.CODABAR,
    Html5QrcodeSupportedFormats.ITF,
];

export const DATA_MATRIX_FORMATS = [Html5QrcodeSupportedFormats.DATA_MATRIX];

type QrBox = { width: number; height: number };

type BarcodeScannerModalProps = {
    open: boolean;
    onClose: () => void;
    onScan: (code: string) => void;
    formats?: Html5QrcodeSupportedFormats[];
    title?: string;
    subtitle?: string;
    qrbox?: QrBox;
    aspectRatio?: number;
    scannerElementId?: string;
    showScanLine?: boolean;
};

const BarcodeScannerModal: FC<BarcodeScannerModalProps> = ({
    open,
    onClose,
    onScan,
    formats = MEDICINE_BARCODE_FORMATS,
    title = 'Scan medicine barcode',
    subtitle = 'Point your camera at the product barcode',
    qrbox = { width: 280, height: 120 },
    aspectRatio = 1.333,
    scannerElementId = DEFAULT_SCANNER_ELEMENT_ID,
    showScanLine = true,
}) => {
    const scannerRef = useRef<Html5Qrcode | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [starting, setStarting] = useState(false);

    const stopScanner = async () => {
        if (scannerRef.current) {
            try {
                await scannerRef.current.stop();
            } catch (_e) {
                // ignore if not running
            }
            try {
                scannerRef.current.clear();
            } catch (_e) {
                // ignore
            }
            scannerRef.current = null;
        }
    };

    useEffect(() => {
        if (!open) {
            stopScanner();
            setError(null);
            setStarting(false);
            return;
        }

        let mounted = true;
        setError(null);
        setStarting(true);

        const startScanner = async () => {
            try {
                const cameras = await Html5Qrcode.getCameras();
                if (!cameras || cameras.length === 0) {
                    if (mounted) setError('No camera found.');
                    return;
                }

                const element = document.getElementById(scannerElementId);
                if (!element || !mounted) return;

                const html5Qrcode = new Html5Qrcode(scannerElementId, {
                    formatsToSupport: formats,
                    verbose: false,
                });
                scannerRef.current = html5Qrcode;

                const preferredBack = cameras.find(
                    (c) => c.label?.toLowerCase().includes('back') || c.label?.toLowerCase().includes('environment')
                );
                const cameraId = preferredBack?.id || cameras[0].id;

                await html5Qrcode.start(
                    cameraId,
                    {
                        fps: 10,
                        qrbox,
                        aspectRatio,
                    },
                    (decodedText) => {
                        if (!mounted || !scannerRef.current) return;
                        stopScanner().then(() => {
                            onScan(decodedText);
                            onClose();
                        });
                    },
                    () => {
                        // No barcode found this frame — ignore
                    }
                );
                if (mounted) setStarting(false);
            } catch (err: any) {
                if (!mounted) return;
                const message =
                    err?.name === 'NotAllowedError' || err?.message?.includes('Permission')
                        ? 'Camera permission denied. Allow camera for this site in the browser, or paste the GS1 code from the package into the form.'
                        : err?.name === 'NotFoundError'
                          ? 'No camera found on this device. Use a USB scanner or paste the GS1 element string manually.'
                          : err?.message || 'Could not start camera. Use a USB scanner or paste the code manually.';
                setError(message);
                setStarting(false);
                scannerRef.current = null;
            }
        };

        startScanner();
        return () => {
            mounted = false;
            stopScanner();
        };
    }, [open]);

    const handleClose = () => {
        stopScanner();
        setError(null);
        onClose();
    };

    if (!open) return null;

    return (
        <Modal
            show={open}
            onHide={handleClose}
            className="z-[1060]"
            dialogClassName="w-full max-w-md overflow-hidden rounded-2xl border-0 bg-white p-0 shadow-xl"
            backdropClassName="fixed inset-0 z-[1059] bg-slate-900/75 backdrop-blur-sm"
        >
            <div className="bg-gradient-to-b from-emerald-600 to-emerald-700 px-6 py-5">
                <h2 className="text-xl font-semibold tracking-tight text-white">{title}</h2>
                <p className="mt-1 text-sm text-emerald-100">{subtitle}</p>
            </div>
            <Modal.Body className="p-0">
                <div className="relative overflow-hidden bg-slate-900">
                    <div
                        id={scannerElementId}
                        className="barcode-scanner-container relative min-h-[240px] w-full [&>div]:!max-w-full [&>video]:!max-w-full"
                        style={{ minHeight: 240 }}
                    />
                    {showScanLine && !error && !starting && (
                        <div
                            className="pointer-events-none absolute left-0 right-0 top-1/2 h-0.5 -translate-y-1/2 bg-emerald-400/80 shadow-[0_0_12px_rgba(52,211,153,0.6)]"
                            aria-hidden
                        />
                    )}
                    {starting && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-900/90">
                            <div className="h-10 w-10 animate-spin rounded-full border-2 border-emerald-400 border-t-transparent" />
                            <p className="text-sm font-medium text-white">Starting camera...</p>
                        </div>
                    )}
                    {error && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-900/95 px-6 py-8 text-center">
                            <div className="rounded-full bg-red-500/20 p-3">
                                <svg
                                    className="h-8 w-8 text-red-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                    />
                                </svg>
                            </div>
                            <p className="text-sm font-medium text-white">{error}</p>
                            <p className="text-xs text-slate-400">
                                You can enter the barcode manually in the form.
                            </p>
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer className="border-t border-slate-200 bg-slate-50 px-6 py-4">
                <ButtonBase
                    innerText="Cancel"
                    type="button"
                    className="w-full rounded-xl border-0 bg-slate-200 px-4 py-2.5 font-medium text-slate-700 hover:bg-slate-300"
                    onClick={handleClose}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default BarcodeScannerModal;
